import type { QueryClient } from '@tanstack/react-query';
import { setProperty } from 'shared/lib/setQueryProperty';
import {
  getProjectsSetterQueryData,
  updateArrayItem,
} from 'shared/lib/updateQueryArrayProp';

import { projectKeys } from '@/enteties/project/api/projects.query-key';
import type {
  BuildStart,
  InstancesChange,
  Project,
  Repository,
  ServiceRunStatus,
  Task,
} from '@/enteties/project/modal';

const repositoryHandler = (data: Repository, queryClient: QueryClient) => {
  if (data.payload.action !== 'CREATE' || !data.payload.success) return;
  setProperty<Project>(
    queryClient,
    projectKeys.project(data.payload.repository),
    {
      status: 'EMPTY',
    }
  );
  const setServicesData = getProjectsSetterQueryData(queryClient);
  updateArrayItem<Project>(
    setServicesData,
    (project) => project.slug === data.payload.repository,
    { status: 'EMPTY' }
  );
};

const taskHandler = (data: Task, queryClient: QueryClient) => {
  let status: Project['status'] = '';
  if (data.payload.stage === 'BUILD') {
    if (data.payload.success) {
      status = 'DEPLOYING';
    } else {
      status = 'BUILD_FAILED';
    }
  } else if (data.payload.stage === 'RUN') {
    if (data.payload.success) {
      status = 'STARTING';
    } else {
      status = 'DEPLOY_FAILED';
    }
  }
  if (!status) return;

  setProperty<Project>(queryClient, projectKeys.project(data.payload.project), {
    status,
    statusMessage: data.payload.message,
  });
  const setServicesData = getProjectsSetterQueryData(queryClient);
  updateArrayItem<Project>(
    setServicesData,
    (project) => project.slug === data.payload.project,
    { status, statusMessage: data.payload.message }
  );
};

const serviceRunStatusHandler = (
  data: ServiceRunStatus,
  queryClient: QueryClient
) => {
  const status = data.payload.message as Project['status'];
  const statusMessage = data.payload.message;

  setProperty<Project>(queryClient, projectKeys.project(data.payload.service), {
    status,
    statusMessage,
  });

  const setServicesData = getProjectsSetterQueryData(queryClient);
  updateArrayItem<Project>(
    setServicesData,
    (project) => project.slug === data.payload.service,
    { status, statusMessage }
  );
};

const instanceChangeHandler = (
  data: InstancesChange,
  queryClient: QueryClient
) => {
  setProperty<Project>(
    queryClient,
    projectKeys.project(data.payload.serviceName),
    {
      instances: data.payload.instances,
    }
  );
  const setServicesData = getProjectsSetterQueryData(queryClient);
  updateArrayItem<Project>(
    setServicesData,
    (project) => project.slug === data.payload.serviceName,
    { instances: data.payload.instances }
  );
};

const buildStartHandler = (data: BuildStart, queryClient: QueryClient) => {
  const status = 'BUILD_STARTED';
  const statusMessage = '';

  setProperty<Project>(queryClient, projectKeys.project(data.payload.project), {
    status,
    statusMessage,
  });
  const setServicesData = getProjectsSetterQueryData(queryClient);
  updateArrayItem<Project>(
    setServicesData,
    (project) => project.slug === data.payload.project,
    { status, statusMessage }
  );
};

export {
  buildStartHandler,
  instanceChangeHandler,
  repositoryHandler,
  serviceRunStatusHandler,
  taskHandler,
};
